import React, { useState, useEffect, useCallback } from "react";
import PanelBreadcrumbs from "../components/panel/PanelBreadcrumbs";
import { BreadcrumbProvider } from "../contexts/BreadcrumbContext";
import { Box, GlobalStyles, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  PANEL,
  panelShellSx,
  panelMainColumnSx,
  panelMainSx,
} from "../design-system/panel";
import PanelThemeProvider, {
  useIsPanelTheme,
} from "../design-system/panel/PanelThemeProvider";

const panelGlobalBodyStyles = {
  body: {
    backgroundColor: PANEL.pageBg,
    backgroundImage: PANEL.pageGradient,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundAttachment: "fixed",
  },
};

export const getSidebarStorageKey = (panelType) =>
  `patientmesh_panel_sidebar_open_${panelType || "default"}`;

function PanelLayoutShell({
  children,
  contentSx = {},
  renderSidebar,
  renderTopbar,
  shellClassName = "panel-shell",
  hideFormAsterisk = false,
  panelType,
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const storageKey = getSidebarStorageKey(panelType);

  const [menuOpen, setMenuOpenState] = useState(() => {
    if (!isDesktop) return false;
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        const saved = window.localStorage.getItem(storageKey);
        if (saved !== null) {
          return saved === "true";
        }
      }
    } catch (e) {
      // ignore storage access errors
    }
    return true;
  });

  useEffect(() => {
    if (isDesktop) {
      try {
        if (typeof window !== "undefined" && window.localStorage) {
          const saved = window.localStorage.getItem(storageKey);
          if (saved !== null) {
            setMenuOpenState(saved === "true");
            return;
          }
        }
      } catch (e) {}
      setMenuOpenState(true);
    } else {
      setMenuOpenState(false);
    }
  }, [isDesktop, storageKey]);

  const setMenuOpen = useCallback(
    (valueOrFn) => {
      setMenuOpenState((prev) => {
        const nextVal =
          typeof valueOrFn === "function" ? valueOrFn(prev) : valueOrFn;
        if (isDesktop) {
          try {
            if (typeof window !== "undefined" && window.localStorage) {
              window.localStorage.setItem(storageKey, String(Boolean(nextVal)));
            }
          } catch (e) {
            // ignore storage access errors
          }
        }
        return nextVal;
      });
    },
    [isDesktop, storageKey]
  );

  const drawerWidth = PANEL.drawer.width;
  const collapsedWidth = PANEL.drawer.collapsed;
  const sidebarWidth = isDesktop
    ? menuOpen
      ? drawerWidth
      : collapsedWidth
    : 0;

  const sidebarProps = {
    menuOpen,
    setMenuOpen,
    variant: isDesktop ? "persistent" : "temporary",
    drawerWidth,
    collapsedWidth,
  };

  return (
    <>
      <GlobalStyles styles={panelGlobalBodyStyles} />
      <Box
        className={shellClassName}
        sx={{
          ...panelShellSx,
          ...(hideFormAsterisk && {
            "& .MuiFormLabel-asterisk": { display: "none" },
          }),
        }}
      >
        {renderSidebar(sidebarProps)}

        <Box
          sx={{
            ...panelMainColumnSx,
            ml: isDesktop ? `${sidebarWidth}px` : 0,
            width: isDesktop ? `calc(100% - ${sidebarWidth}px)` : "100%",
            maxWidth: isDesktop ? `calc(100% - ${sidebarWidth}px)` : "100%",
            minWidth: 0,
            boxSizing: "border-box",
            transition: "margin 0.3s ease, width 0.3s ease, max-width 0.3s ease",
          }}
        >
          {renderTopbar({ menuOpen, setMenuOpen })}

          <Box
            component="main"
            sx={{
              ...panelMainSx,
              ...contentSx,
            }}
          >
            <BreadcrumbProvider>
              {panelType ? <PanelBreadcrumbs panelType={panelType} /> : null}
              {children}
            </BreadcrumbProvider>
          </Box>
        </Box>
      </Box>
    </>
  );
}

/**
 * Ortak panel layout iskeleti — sidebar + topbar + main content.
 * Nested ThemeProvider ile MUI bu route'larda PANEL temasında çalışır.
 * DoctorLayout / TenantLayout zaten sarmaladıysa ikinci provider açılmaz.
 */
export default function PanelLayout(props) {
  const alreadyPanel = useIsPanelTheme();
  const shell = <PanelLayoutShell {...props} />;
  if (alreadyPanel) return shell;
  return <PanelThemeProvider>{shell}</PanelThemeProvider>;
}
